import { useMemo } from 'react';
import { CampaignPlanFilterOptions } from 'components/common/types/CampaignPlan.types';
import { AVAILABLE_COLOR, AVAILABLE_COLOR_RGB } from 'components/pages/Planner/PlannerMap/utils/mapColors';

export interface FilterColor {
  hex: string;
  rgb: number[];
}

export type FilterColorMap = Record<string, FilterColor>;

const GOLDEN_ANGLE = 137.508;
const SATURATION = 70;
const LIGHTNESS_DARK = 30;
const LIGHTNESS_LIGHT = 45;

const hslToRgb = (hue: number, saturation: number, lightness: number): number[] => {
  const s = saturation / 100;
  const l = lightness / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): number => {
    const k = (n + hue / 30) % 12;
    return l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
  };
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
};

const rgbToHex = (rgb: number[]): string => `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`;

const buildColorMap = (values: string[], startHue: number): FilterColorMap =>
  values.reduce<FilterColorMap>((acc, value, index) => {
    const hue = (startHue + index * GOLDEN_ANGLE) % 360;
    const lightness = index % 2 === 0 ? LIGHTNESS_DARK : LIGHTNESS_LIGHT;
    const rgb = hslToRgb(hue, SATURATION, lightness);
    acc[value] = { hex: rgbToHex(rgb), rgb };
    return acc;
  }, {});

export const useFilterColors = (
  filterOptions: CampaignPlanFilterOptions | null,
): { channelColors: FilterColorMap; formatColors: FilterColorMap; mediaTypeColors: FilterColorMap } => {
  const channels = filterOptions?.channel ?? [];
  const formats = filterOptions?.format ?? [];
  const mediaTypes = filterOptions?.mediaType ?? [];

  return useMemo(() => {
    const allValues = [...channels, ...formats];
    const allColors = buildColorMap(allValues, 0);

    const channelColors: FilterColorMap = {};
    channels.forEach((key) => {
      channelColors[key] = allColors[key];
    });

    const formatColors: FilterColorMap = {};
    formats.forEach((key) => {
      formatColors[key] = allColors[key];
    });

    const mediaTypeColors: FilterColorMap = {};
    mediaTypes.forEach((mediaType) => {
      mediaTypeColors[mediaType] = {
        hex: AVAILABLE_COLOR,
        rgb: [...AVAILABLE_COLOR_RGB],
      };
    });

    return {
      channelColors,
      formatColors,
      mediaTypeColors,
    };
  }, [channels, formats, mediaTypes]);
};
